import { ComponentType } from 'react';

import { ds } from '@/components/lib/design.system';

import { DoubleRowHeroLayout } from '../components/Layout/DoubleRowHeroLayout';
import { DoubleRowLayout } from '../components/Layout/DoubleRowLayout';
import { FeaturedCarouselLayout } from '../components/Layout/FeaturedCarouselLayout';
import { FeaturedGalleryLayout } from '../components/Layout/FeaturedGalleryLayout';
import { FeaturedGridLayout } from '../components/Layout/FeaturedGridLayout';
import { FeaturedRowLayout } from '../components/Layout/FeaturedRowLayout';
import { GridListToggleLayout } from '../components/Layout/GridListToggleLayout';
import { SingleRowLayout } from '../components/Layout/SingleRowLayout';
import { TrendingLayout } from '../components/Layout/TrendingLayout';
import { CasinoGame } from '../hooks/use-casino-games';
import { JackpotPoolResponse } from '../hooks/use-casino-jackpot-pool';
import { LayoutHeaderProps } from '../hooks/use-layout-header-link';

export interface CasinoLayoutProps {
    data: CasinoGame[];
    isFetchingNextPage?: boolean;
    layoutHeaderProps?: LayoutHeaderProps;
}

/** Casino game card tag variants. `generic` is the default grey pill any other label falls back to. */
export const CASINO_TAG_COLORS = {
    generic: ds.colorToken.background.neutral.stronger,
    new: ds.colorToken.background.discovery.strong,
    exclusive: ds.colorToken.background.brand.emphasis,
} as const;

export const getCasinoGameTag = (game: CasinoGame, customLabel?: string) => {
    if (customLabel) {
        return { label: customLabel, color: CASINO_TAG_COLORS.generic };
    }
    if (game.isNew) {
        return { label: 'New', color: CASINO_TAG_COLORS.new };
    }
    if (game.exclusive?.effective) {
        return { label: 'Exclusive', color: CASINO_TAG_COLORS.exclusive };
    }
    return undefined;
};

export const JACKPOT_POOL_CURRENCY = 'SSC';
const JACKPOT_POOL_VENDOR = 'Playson';

export const isJackpotGroupId = (groupId: string) => groupId.toLowerCase().includes('jackpot');

export const isJackpotGame = ({ groups, vendor }: CasinoGame) =>
    vendor?.name === JACKPOT_POOL_VENDOR && Boolean(groups?.items.some(isJackpotGroupId));

export const getJackpotPoolAmount = (response: JackpotPoolResponse) => {
    const jackpot = response.jackpots.find(pool => pool.currency === JACKPOT_POOL_CURRENCY);
    const subunits = Number(jackpot?.progressive_amount ?? jackpot?.amount);
    return subunits > 0 ? subunits / 100 : undefined;
};

export const LAYOUT_MAP: Record<string, ComponentType<CasinoLayoutProps>> = {
    'single-row': SingleRowLayout,
    trending: TrendingLayout,
    'double-row': DoubleRowLayout,
    'double-row-hero': DoubleRowHeroLayout,
    'featured-row': FeaturedRowLayout,
    'featured-gallery': FeaturedGalleryLayout,
    'featured-carousel': FeaturedCarouselLayout,
    'featured-grid': FeaturedGridLayout,
    'grid-list-toggle': GridListToggleLayout,
};

export const getLayoutComponent = (flag?: string): ComponentType<CasinoLayoutProps> =>
    (flag && LAYOUT_MAP[flag.toLowerCase()]) || SingleRowLayout;

// Identifiers for the special (non-API) tabs shown in the casino lobby TabBar.
export const LOBBY_TAB_ID = 'lobby';
export const FAVORITES_TAB_ID = 'favorites';
export const FREE_SPINS_TAB_ID = 'free-spins';

/**
 * Checks if a category has sub categories (i.e. is navigable from the top navigation).
 *
 * @param title - the category display name
 * @param nonNavigableCategories - category keys/names (case-insensitive substrings) that are not navigable
 */
export const isNavigableCategory = (title: string, nonNavigableCategories: readonly string[]): boolean => {
    const lowerTitle = title.toLowerCase();
    return !nonNavigableCategories.some(category => lowerTitle.includes(category.toLowerCase()));
};
